import { useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useAdDetail } from "@/app/hooks/use-ad-detail";
import { CartPage } from "@/app/components/cart-page";
import { CheckoutAddressPage } from "@/app/components/checkout-address-page";
import { CheckoutShippingPage } from "@/app/components/checkout-shipping-page";
import { CheckoutPaymentPage } from "@/app/components/checkout-payment-page";
import { CheckoutInstallmentsPage } from "@/app/components/checkout-installments-page";
import { CheckoutReviewPage } from "@/app/components/checkout-review-page";
import { CheckoutStripePage } from "@/app/components/checkout-stripe-page";
import { CheckoutSecondPartyPage } from "@/app/components/checkout-second-party-page";
import { OrderConfirmationPage } from "@/app/components/order-confirmation-page";
import { CartToast } from "@/app/components/cart-toast";
import { PageLayout } from "@/app/components/page-layout";
import { FloatingChatButton } from "@/app/components/floating-chat-button";
import { SortSheet } from "@/app/components/sort-sheet";
import { CustomFilterSheet } from "@/app/components/custom-filter-sheet";
import { LocationSelectorModal } from "@/app/components/location-selector-modal";
import { NotificationsPage } from "@/app/components/notifications-page";
import { AddressesPage } from "@/app/components/addresses-page";
import { MyAdsPage } from "@/app/components/my-ads-page";
import { FavoritesPage } from "@/app/components/favorites-page";
import { AdDetailPage } from "@/app/components/ad-detail-page";
import { SellerProfilePage } from "@/app/components/seller-profile-page";
import { ChatListPage } from "@/app/components/chat-list-page";
import { ChatPage } from "@/app/components/chat-page";
import { CategoryResultsPage } from "@/app/components/category-results-page";
import { ProfilePage } from "@/app/components/profile-page";
import { EditProfilePage } from "@/app/components/edit-profile-page";
import { SettingsPage } from "@/app/components/settings-page";
import { HelpSupportPage } from "@/app/components/help-support-page";
import { TrocaViaHubPage } from "@/app/components/troca-via-hub-page";
import { WalletPage } from "@/app/components/wallet-page";
import { PasswordSecurityPage } from "@/app/components/password-security-page";
import { PaymentMethodsPage } from "@/app/components/payment-methods-page";
import { WithdrawPage } from "@/app/components/withdraw-page";
import { LanguageRegionPage } from "@/app/components/language-region-page";
import { PrivacyPage } from "@/app/components/privacy-page";
import { DataUsagePage } from "@/app/components/data-usage-page";
import { AboutPage } from "@/app/components/about-page";
import { TermsPage } from "@/app/components/terms-page";
import { PrivacyPolicyPage } from "@/app/components/privacy-policy-page";
import { AccountDeletionPublicPage } from "@/app/components/account-deletion-public-page";
import { MakeOfferPage } from "@/app/components/make-offer-page";
import { ReceivedOffersPage } from "@/app/components/received-offers-page";
import { SentOffersPage } from "@/app/components/sent-offers-page";
import { SalesPage } from "@/app/components/sales-page";
import { PurchasesPage } from "@/app/components/purchases-page";
import { OrderDetailPage } from "@/app/components/order-detail-page";
import type { PageType } from "@/app/hooks/use-app-state";
import type { Ad } from "@/app/components/ad-card";
import type { FavoriteItem } from "@/app/components/favorites-page";
import type { CartItem } from "@/app/components/cart-page";
import type { Address } from "@/app/components/checkout-address-page";
import type { PaymentMethod } from "@/app/components/checkout-payment-page";
import type { ShippingOption, ShippingRouteLabels } from "@/app/components/checkout-shipping-page";
import type { Conversation } from "@/app/components/chat-list-page";
import type { OfferData } from "@/app/components/make-offer-page";
import type { SortOption } from "@/app/components/sort-sheet";
import type { CustomFilters } from "@/app/components/custom-filter-sheet";
import type { City } from "@/app/components/location-selector-modal";
import type { OnboardingData } from "@/app/components/new-user-onboarding";
import type { EditProfileFormData } from "@/app/utils/profile-mapper";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface CommonPageProps {
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  /** Dim Anunciar + same gate as handleRequestCreateAd when email not verified after onboarding. */
  postAdBlockedByUnverifiedEmail?: boolean;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
}

interface AppRouterProps {
  currentPage: PageType;
  commonPageProps: CommonPageProps;
  ads: Ad[];
  /** Server-side search results for category-results page (GET /ads/search). */
  searchResultsAds?: Ad[];
  searchNextCursor?: string | null;
  searchResultsLoading?: boolean;
  loadSearchResults?: () => void;
  loadMoreSearchResults?: () => void;
  /** Current user's ads (from GET /auth/me/ads), used for make-offer "Produto + Dinheiro". */
  myAdsForOffer?: Ad[];
  /** True while my ads are being loaded for make-offer (avoids flashing "precisa ter anúncio ativo"). */
  myAdsForOfferLoading?: boolean;
  selectedAdId: string | null;
  selectedOrderId: string | null;
  selectedSellerName: string | null;
  selectedSellerId: string | null;
  favoriteItems: FavoriteItem[];
  favoriteAdIds: string[];
  favoriteCreatedAt: Record<string, string>;
  cart: CartItem[];
  addresses: Address[];
  selectedAddressId: string | null;
  shippingOptions: ShippingOption[];
  shippingOptionsLoading?: boolean;
  shippingOptionsError?: string | null;
  loadShippingOptions?: (
    cep: string,
    items: Array<{ ad_id: string; quantity: number }>,
    opts?: { cartOverride?: Array<{ isOfferItem?: boolean; offerId?: string; ad?: { id: string; shipping_origin_cep?: string }; offerTradeInAds?: Array<{ id: string }> }> }
  ) => Promise<unknown>;
  /** Hub city/state from BFF/config-platform for route visualization (when hub routing). */
  hubDisplay?: { city: string; state: string } | null;
  selectedShippingId: string | null;
  paymentMethods: PaymentMethod[];
  selectedPaymentId: string | null;
  selectedInstallments: number;
  lastOrderNumber: string;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => void;
  selectedConversationId: string | null;
  chatMessages: any[]; // Array of chat messages
  receivedOffers: any[];
  /** Load received offers from API (called when Received offers page mounts). */
  /** Load received offers; may return the list for callers that need it (e.g. after 400). */
  loadReceivedOffers?: () => Promise<unknown>;
  sentOffers: any[];
  /** Load sent offers from API (called when Sent offers page mounts). */
  /** Load sent offers from BFF and update state. */
  loadSentOffers?: () => Promise<unknown>;
  selectedOfferId: string | null;
  showCartToast: boolean;
  cartToastProduct: string;
  isModalOpen: boolean;
  isMenuOpen: boolean;
  isChatModalOpen: boolean;
  onOpenChatModal: () => void;
  onCloseChatModal: () => void;
  totalUnreadMessages: number;
  /** Notifications bell badge count (from BFF). */
  notificationUnreadCount: number;
  onUnreadCountChange: (count: number) => void;
  /** Call after offer/chat actions that may create notifications, to refresh the bell badge without opening the notifications page. */
  onNotificationCountRefetch?: () => void;
  onSetCurrentPage: (page: PageType) => void;
  onNavigateBack: () => void;
  /** Set return context so back from ad-detail goes to this page (used when opening ad from list pages). */
  onSetReturnToPage?: (page: PageType | null) => void;
  /** When user taps a notification with a link (e.g. offer), navigate to that path. */
  onNavigateToNotificationLink?: (link: string) => void;
  onSetSelectedAdId: (id: string | null) => void;
  /** Open order detail. Pass returnPage "sales" when navigating from Minhas Vendas so Back returns to sales. */
  onOpenOrderDetail: (orderId: string, returnPage?: "purchases" | "sales") => void;
  orderDetailReturnPage: "purchases" | "sales";
  onSetSelectedOrderId: (id: string | null) => void;
  onSetSelectedConversationId: (id: string | null) => void;
  onToggleFavorite: (ad: Ad) => void;
  onRemoveFavorite: (id: string) => void;
  onAddToCart: (item: Ad | any) => void; // Accepts both Ad string ID and CartItem object
  onRemoveFromCart: (id: string) => void;
  isInCart: (id: string) => boolean;
  isFavorite: (id: string) => boolean;
  onUpdateCartQuantity: (id: string, quantity: number) => void;
  onSelectAddress: (id: string) => void;
  onSelectShipping: (id: string) => void;
  /** When provided, used for "Continuar para Revisão" on shipping step (patches linked order with selected shipping then navigates). */
  onShippingContinueToReview?: () => void | Promise<void>;
  onSelectPayment: (id: string) => void;
  onSelectInstallments: (installments: number) => void;
  onAddNewPaymentMethod: (method: Omit<PaymentMethod, "id">) => void;
  onConfirmOrder: () => void;
  /** Error from create-order (e.g. validation or network). Shown on checkout-review. */
  orderError?: string | null;
  orderConfirming?: boolean;
  /** Stripe payment step (after create order). */
  pendingPaymentOrderId?: string | null;
  pendingPaymentClientSecret?: string | null;
  pendingPaymentPublishableKey?: string;
  onPaymentSuccess?: (orderId: string) => void | Promise<void>;
  onBackFromStripeCheckout?: () => void;
  /** Open Stripe checkout for an existing pending-payment order (from order detail). */
  onPayPendingOrder?: (orderId: string) => void | Promise<void>;
  /** Go to checkout-review to pay existing order (same flow as cart → review → pay). */
  onGoToCheckoutToPayOrder?: (orderId: string) => void;
  /** From checkout-review in pay-order mode: get payment intent and go to Stripe. */
  onPayOrderFromReview?: (orderId: string) => void | Promise<void>;
  /** When set, checkout-review is in "pay existing order" mode. */
  payOrderId?: string | null;
  setPayOrderId?: (id: string | null) => void;
  /** When set, cart is tied to this order (resume checkout); review shows order data and confirm pays this order. */
  cartOrderId?: string | null;
  /** When set, user is in second-party checkout (swap: pay only shipping). */
  secondPartyPaymentOrderId?: string | null;
  setSecondPartyPaymentOrderId: (id: string | null) => void;
  /** Called when second-party page gets payment intent; parent sets pending payment state and navigates to checkout-stripe. */
  onSecondPartyPaymentIntentReady?: (orderId: string, clientSecret: string, publishableKey: string) => void;
  onChatWithSeller: (adId: string, adTitle: string, sellerId?: string) => void;
  onSendMessage: (conversationId: string, message: string) => void;
  onCloseCartToast: () => void;
  onCreateAd: (ad: Omit<Ad, "id" | "createdAt">) => void;
  onAdDeleted?: () => void;
  onCloseModal: () => void;
  onOpenModal: () => void;
  /** Increment to refetch My Ads list (e.g. after creating an ad). */
  myAdsRefreshKey?: number;
  onCloseMenu: () => void;
  menuHandlers: {
    onLogout?: () => void;
    onNavigateToNotifications: () => void;
    onNavigateToAddresses: () => void;
    onNavigateToMyAds: () => void;
    onNavigateToProfile: () => void;
    onNavigateToSettings?: () => void;
    onNavigateToHelp?: () => void;
    onNavigateToReceivedOffers?: () => void;
    onNavigateToSentOffers?: () => void;
    onNavigateToFavorites?: () => void;
    setSelectedSellerName: (name: string) => void;
    setSelectedSellerId: (id: string | null) => void;
    onNavigateToChat?: () => void;
    onNavigateToSales?: () => void;
    onNavigateToPurchases?: () => void;
    onNavigateToWallet?: () => void;
  };
  onSaveProfile?: (data: EditProfileFormData) => void | Promise<void>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string;
  /** Resolved name for selectedCategory (catalog category_id → name); used for page title when set. */
  categoryDisplayName?: string;
  /** Flat catalog for category-results empty-state label resolution (when display name is still cat_*). */
  categoryCatalog?: import("@/app/api/auth-client").Category[] | null;
  onCategoryChange: (category: string) => void;
  categories: string[];
  /** For footer "Categorias Populares" (top 5 by ad count when ranked; else A–Z slice). */
  popularCategories?: { categoryId: string; name: string }[];
  currentLocation: string;
  onLocationClick: () => void;
  onSortClick: () => void;
  onCustomFilterClick: () => void;
  activeFiltersCount: number;
  currentSort: SortOption;
  customFilters: CustomFilters;
  onBackToHome: () => void;
  isSortSheetOpen: boolean;
  onCloseSortSheet: () => void;
  onSortChange: (sort: SortOption) => void;
  isCustomFilterOpen: boolean;
  onCloseCustomFilter: () => void;
  onCustomFilterChange: (filters: CustomFilters) => void;
  onClearCustomFilters: () => void;
  isLocationSelectorOpen: boolean;
  onCloseLocationSelector: () => void;
  selectedCity: City | null;
  onSelectLocation: (city: City) => void;
  onClearNavigatedFromMenu: () => void;
  onNavigateBackToSettings: () => void;
  onNavigateToPasswordSecurity: () => void;
  onNavigateToLanguageRegion: () => void;
  onNavigateToAbout: () => void;
  onNavigateToTerms: () => void;
  onNavigateToPrivacyPolicy: () => void;
  onNavigateToPrivacy: () => void;
  onNavigateToAccountDeletionPublic: () => void;
  onMakeOffer?: () => void;
  onSubmitOffer?: (offer: OfferData) => void;
  isSubmittingOffer?: boolean;
  onViewOfferDetails?: (offerId: string, sender: "user" | "other") => void;
  /** Current user id (from auth); used to hide cart/offer/chat when viewer is the ad seller. */
  userId?: string;
  userProfile?: OnboardingData | null;
  userEmail?: string;
  /** Profile photo URL for menu avatar */
  userProfileImageUrl?: string;
  /** Name from GET /auth/me (fallback when profile row not loaded or has no display name). */
  authUserName?: string;
  /** Called when profile is updated from My Addresses (so app can refresh default address). */
  onProfileUpdated?: (profile: import("@/app/api/auth-client").Profile) => void;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}

export function AppRouter({
  currentPage,
  commonPageProps,
  ads,
  searchResultsAds = [],
  searchNextCursor = null,
  searchResultsLoading = false,
  loadSearchResults: _loadSearchResults,
  loadMoreSearchResults,
  myAdsForOffer = [],
  myAdsForOfferLoading = false,
  selectedAdId,
  selectedOrderId,
  selectedSellerName,
  selectedSellerId,
  favoriteItems,
  favoriteAdIds,
  favoriteCreatedAt,
  cart,
  addresses,
  selectedAddressId,
  shippingOptions,
  shippingOptionsLoading = false,
  shippingOptionsError = null,
  loadShippingOptions,
  hubDisplay = null,
  selectedShippingId,
  paymentMethods,
  selectedPaymentId,
  selectedInstallments,
  lastOrderNumber,
  conversations,
  setConversations,
  selectedConversationId,
  chatMessages,
  receivedOffers,
  loadReceivedOffers,
  sentOffers,
  loadSentOffers,
  selectedOfferId: _selectedOfferId,
  showCartToast,
  cartToastProduct,
  isModalOpen,
  isMenuOpen,
  isChatModalOpen: _isChatModalOpen,
  onOpenChatModal,
  onCloseChatModal: _onCloseChatModal,
  totalUnreadMessages,
  notificationUnreadCount,
  onUnreadCountChange,
  onNotificationCountRefetch,
  onSetCurrentPage,
  onNavigateBack,
  onSetReturnToPage,
  onNavigateToNotificationLink,
  onSetSelectedAdId,
  onOpenOrderDetail,
  orderDetailReturnPage,
  onSetSelectedOrderId,
  onSetSelectedConversationId,
  onToggleFavorite,
  onRemoveFavorite,
  onAddToCart,
  onRemoveFromCart,
  isInCart,
  isFavorite,
  onUpdateCartQuantity,
  onSelectAddress,
  onSelectShipping,
  onShippingContinueToReview,
  onSelectPayment,
  onSelectInstallments,
  onAddNewPaymentMethod,
  onConfirmOrder,
  orderError,
  orderConfirming,
  pendingPaymentOrderId,
  pendingPaymentClientSecret,
  pendingPaymentPublishableKey,
  onPaymentSuccess,
  onBackFromStripeCheckout,
  onPayPendingOrder,
  onGoToCheckoutToPayOrder,
  onPayOrderFromReview,
  payOrderId = null,
  setPayOrderId,
  cartOrderId = null,
  secondPartyPaymentOrderId = null,
  setSecondPartyPaymentOrderId,
  onSecondPartyPaymentIntentReady,
  onChatWithSeller,
  onSendMessage,
  onCloseCartToast,
  onCreateAd,
  onAdDeleted,
  onCloseModal,
  onOpenModal,
  onCloseMenu,
  menuHandlers,
  myAdsRefreshKey = 0,
  // Category results page props
  searchQuery,
  onSearchChange,
  selectedCategory,
  categoryDisplayName,
  categoryCatalog = null,
  onCategoryChange,
  categories,
  popularCategories = [],
  currentLocation,
  onLocationClick,
  onSortClick,
  onCustomFilterClick,
  activeFiltersCount,
  currentSort,
  customFilters,
  onBackToHome,
  // Sheet state management
  isSortSheetOpen,
  onCloseSortSheet,
  onSortChange,
  isCustomFilterOpen,
  onCloseCustomFilter,
  onCustomFilterChange,
  onClearCustomFilters,
  isLocationSelectorOpen,
  onCloseLocationSelector,
  selectedCity,
  onSelectLocation,
  onClearNavigatedFromMenu,
  onNavigateBackToSettings,
  onNavigateToPasswordSecurity,
  onNavigateToLanguageRegion,
  onNavigateToAbout,
  onNavigateToTerms,
  onNavigateToPrivacyPolicy,
  onNavigateToPrivacy,
  onNavigateToAccountDeletionPublic,
  onMakeOffer,
  onSubmitOffer,
  isSubmittingOffer = false,
  onViewOfferDetails,
  userId,
  userProfile,
  userEmail,
  userProfileImageUrl,
  authUserName,
  onSaveProfile,
  onProfileUpdated,
  isDarkMode,
  onToggleDarkMode,
}: AppRouterProps) {
  const accountNav = useAccountNavEnabled();
  const displayName = userProfile?.preferredName?.trim() || userProfile?.fullName || "";
  const fullName = userProfile?.fullName?.trim() || "";
  const displayInitials = fullName
    ? fullName.split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase()
    : "";

  const settingsCardName =
    userProfile?.preferredName?.trim() ||
    userProfile?.fullName?.trim() ||
    authUserName?.trim() ||
    (userEmail?.includes("@") ? userEmail.split("@")[0] : "") ||
    "Usuário";
  const settingsCardEmail = userEmail?.trim() ?? "";
  const settingsCardInitials = (() => {
    const base =
      userProfile?.fullName?.trim() ||
      userProfile?.preferredName?.trim() ||
      authUserName?.trim() ||
      "";
    if (base) {
      const parts = base.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return base.slice(0, 2).toUpperCase();
    }
    const em = userEmail?.trim();
    return em && em[0] ? em[0].toUpperCase() : "?";
  })();

  const layoutProps = {
    isModalOpen,
    onCloseModal,
    onSubmitAd: onCreateAd,
    isMenuOpen,
    onCloseMenu,
    isLoggedIn: !!userId,
    userName: displayName,
    userEmail,
    userInitials: displayInitials,
    userProfileImageUrl,
    ...menuHandlers,
  };

  type SubPageCommonProps = Omit<typeof commonPageProps, "activeTab"> & {
    activeTab: "home" | "favorites";
  };
  const subPageCommonProps: SubPageCommonProps = {
    ...commonPageProps,
    activeTab: (commonPageProps.activeTab === "wallet" ? "home" : commonPageProps.activeTab) as "home" | "favorites",
  };

  const wrapToggleFavorite = (adId: string): void => {
    const ad = ads.find((a) => a.id === adId);
    if (ad) onToggleFavorite(ad);
  };

  // Floating chat only with full profile (same hub as cart / menu)
  const showFloatingChatButton =
    accountNav && !!userId && currentPage !== "chat" && currentPage !== "chat-conversation";

  // Fetch full ad by id when on ad-detail or make-offer (list only has card payload)
  const { ad: detailAd, loading: detailAdLoading, error: detailAdError } = useAdDetail(
    currentPage === "ad-detail" || currentPage === "make-offer" ? selectedAdId : null
  );

  // Handle checkout-review redirect if missing data (address + shipping required; payment is chosen on Stripe page). Skip when paying an existing order (payOrderId or cartOrderId set).
  useEffect(() => {
    if (currentPage === "checkout-review" && !payOrderId && !cartOrderId) {
      const selectedAddress = addresses.find(a => a.id === selectedAddressId);
      const selectedShipping = shippingOptions.find(s => s.id === selectedShippingId);
      if (!selectedAddress || !selectedShipping) {
        onSetCurrentPage("checkout-address");
      }
    }
  }, [currentPage, payOrderId, cartOrderId, addresses, selectedAddressId, shippingOptions, selectedShippingId, onSetCurrentPage]);

  // Redirect to checkout-address if user lands on checkout-shipping without a selected address
  useEffect(() => {
    if (currentPage === "checkout-shipping" && !selectedAddressId) {
      onSetCurrentPage("checkout-address");
    }
  }, [currentPage, selectedAddressId, onSetCurrentPage]);

  // Stable key so we do not re-fetch on cart/addresses reference churn; refs carry latest values into the debounced call.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const addressesRef = useRef(addresses);
  addressesRef.current = addresses;
  const selectedAddressIdRef = useRef(selectedAddressId);
  selectedAddressIdRef.current = selectedAddressId;
  const loadShippingOptionsRef = useRef(loadShippingOptions);
  loadShippingOptionsRef.current = loadShippingOptions;

  const checkoutShippingQuoteKey = useMemo(() => {
    const addr = addresses.find((a) => a.id === selectedAddressId);
    const cep = (addr?.zipCode ?? "").replace(/\D/g, "").trim();
    if (cep.length < 8 || cart.length === 0) return "";
    const part = [...cart]
      .sort((a, b) => a.ad.id.localeCompare(b.ad.id))
      .map((i) => `${i.ad.id}:${i.quantity}`)
      .join(",");
    return `${cep.slice(0, 8)}|${part}`;
  }, [cart, addresses, selectedAddressId]);

  // Load shipping options from BFF when entering checkout-shipping (CEP from selected address + cart)
  useEffect(() => {
    if (currentPage !== "checkout-shipping" || !checkoutShippingQuoteKey) return;
    const t = window.setTimeout(() => {
      const addr = addressesRef.current.find((a) => a.id === selectedAddressIdRef.current);
      const cepRaw = (addr?.zipCode ?? "").replace(/\D/g, "").trim();
      if (cepRaw.length < 8) return;
      const cep = cepRaw.slice(0, 8);
      const items = cartRef.current.map((item) => ({ ad_id: item.ad.id, quantity: item.quantity }));
      if (items.length === 0) return;
      loadShippingOptionsRef.current?.(cep, items);
    }, 320);
    return () => window.clearTimeout(t);
  }, [currentPage, checkoutShippingQuoteKey]);

  // Refresh sent offers when viewing ad detail so "Enviar Oferta" reflects latest status (e.g. after seller declined)
  useEffect(() => {
    if (currentPage === "ad-detail" && selectedAdId && loadSentOffers) loadSentOffers();
  }, [currentPage, selectedAdId, loadSentOffers]);

  switch (currentPage) {
    case "notifications":
      return (
        <>
          <PageLayout {...layoutProps}>
            <NotificationsPage
              onBack={onNavigateBack}
              onUnreadCountChange={onUnreadCountChange}
              onNavigateToLink={onNavigateToNotificationLink}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "addresses":
      return (
        <>
          <PageLayout {...layoutProps}>
            <AddressesPage onBack={onNavigateBack} onProfileUpdated={onProfileUpdated} {...subPageCommonProps} />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "my-ads":
      return (
        <>
          <PageLayout {...layoutProps}>
            <MyAdsPage onBack={onNavigateBack} onCreateAd={onOpenModal} onAdDeleted={onAdDeleted} refreshTrigger={myAdsRefreshKey} {...subPageCommonProps} />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "favorites":
      return (
        <>
          <PageLayout {...layoutProps}>
            <FavoritesPage
              onBack={onNavigateBack}
              favoriteItems={favoriteItems}
              favoriteAdIds={favoriteAdIds}
              favoriteCreatedAt={favoriteCreatedAt}
              onRemoveFavorite={onRemoveFavorite}
              onToggleFavorite={wrapToggleFavorite}
              onAddToCart={onAddToCart}
              onRemoveFromCart={onRemoveFromCart}
              isInCart={isInCart}
              onViewAdDetail={(adId) => {
                onSetReturnToPage?.(currentPage);
                onSetSelectedAdId(adId);
                onSetCurrentPage("ad-detail");
              }}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "ad-detail": {
      if (!selectedAdId) return null;
      if (detailAdLoading) {
        return (
          <PageLayout {...layoutProps}>
            <div className="flex min-h-[50vh] items-center justify-center">
              <p className="text-muted-foreground">Carregando…</p>
            </div>
          </PageLayout>
        );
      }
      if (detailAdError || !detailAd) {
        return (
          <PageLayout {...layoutProps}>
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
              <p className="text-muted-foreground">
                {detailAdError ? "Não foi possível carregar o anúncio." : "Anúncio não encontrado."}
              </p>
              <button
                type="button"
                onClick={onNavigateBack}
                className="text-primary underline"
              >
                Voltar
              </button>
            </div>
          </PageLayout>
        );
      }
      const ad = detailAd;
      const isSeller = !!(ad.seller_id && userId && ad.seller_id === userId);
      return (
        <>
          <PageLayout {...layoutProps}>
            <AdDetailPage
              ad={ad}
              onBack={onNavigateBack}
              isFavorite={isFavorite(ad.id)}
              onToggleFavorite={wrapToggleFavorite}
              isInCart={isInCart(ad.id)}
              onAddToCart={onAddToCart}
              onRemoveFromCart={onRemoveFromCart}
              onChatWithSeller={() => onChatWithSeller(ad.id, ad.title, ad.seller_id)}
              onSellerClick={(sellerId, sellerName) => {
                menuHandlers.setSelectedSellerId(sellerId || null);
                menuHandlers.setSelectedSellerName(sellerName);
                onSetCurrentPage("seller-profile");
              }}
              onMakeOffer={onMakeOffer}
              isSeller={isSeller}
              hasPendingOffer={sentOffers.some((o) => o.targetAd?.id === selectedAdId && o.status === "pending")}
              {...subPageCommonProps}
            />
            {accountNav && (
              <CartToast
                isOpen={showCartToast}
                onClose={onCloseCartToast}
                onGoToCart={() => {
                  onCloseCartToast();
                  onSetCurrentPage("cart");
                }}
                productName={cartToastProduct}
              />
            )}
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "seller-profile": {
      if (!selectedSellerId) return null;
      return (
        <>
          <PageLayout {...layoutProps}>
            <SellerProfilePage
              sellerId={selectedSellerId}
              sellerName={selectedSellerName}
              onBack={onNavigateBack}
              onAdClick={(ad) => {
                onSetSelectedAdId(ad.id);
                onSetCurrentPage("ad-detail");
              }}
              onToggleFavorite={wrapToggleFavorite}
              isFavorite={isFavorite}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "cart":
      return (
        <>
          <PageLayout {...layoutProps}>
            <CartPage
              onBack={onNavigateBack}
              cartItems={cart}
              onRemoveItem={onRemoveFromCart}
              onUpdateQuantity={onUpdateCartQuantity}
              onProceedToCheckout={() => {
                onSetReturnToPage?.("cart");
                onSetCurrentPage("checkout-address");
              }}
              onPostAdClick={commonPageProps.onPostAdClick}
              postAdBlockedByUnverifiedEmail={commonPageProps.postAdBlockedByUnverifiedEmail}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "checkout-address":
      return (
        <>
          <PageLayout {...layoutProps}>
            <CheckoutAddressPage
              onBack={onNavigateBack}
              addresses={addresses}
              selectedAddressId={selectedAddressId}
              onSelectAddress={onSelectAddress}
              onAddNewAddress={() => onSetCurrentPage("addresses")}
              onContinue={() => {
                onSetReturnToPage?.("checkout-address");
                onSetCurrentPage("checkout-shipping");
              }}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "checkout-shipping": {
      const hasHubRoutingFromOptions = shippingOptions.some((o) => o.buyerAmount != null);
      // Show hub route (Trajeto do Envio) only for swap: offer with product trade-ins, not cash-only offers.
      const isHubFlowFromCart = cart.some(
        (i: { isOfferItem?: boolean; offerTradeInAds?: unknown[] }) =>
          i.isOfferItem && Array.isArray(i.offerTradeInAds) && i.offerTradeInAds.length > 0
      );
      const selectedAddress = addresses.find((a) => a.id === selectedAddressId);
      let routeLabels: ShippingRouteLabels | null = null;
      // Hub flow (buyer): origin = buyer's address (where they ship their item from), hub, destination = seller's address (where buyer's item is delivered). For swap, destination is seller's location; otherwise selected delivery address.
      if ((hasHubRoutingFromOptions || isHubFlowFromCart) && hubDisplay && selectedAddress) {
        const buyerOriginAddress = addresses.find((a) => a.isDefault) ?? addresses[0] ?? selectedAddress;
        const originLabel = [buyerOriginAddress.city, buyerOriginAddress.state].filter(Boolean).join(" / ") || "Origem";
        const offerItem = cart.find(
          (i: { isOfferItem?: boolean; offerTradeInAds?: unknown[]; ad?: { location?: string } }) =>
            i.isOfferItem && Array.isArray(i.offerTradeInAds) && i.offerTradeInAds.length > 0 && i.ad?.location
        ) as { ad: { location: string } } | undefined;
        // Show only city and state for destination (strip neighborhood, e.g. "Centro, Restinga, SP" → "Restinga, SP")
        const rawDest = offerItem?.ad?.location?.trim();
        const destinationLabel = rawDest
          ? (() => {
              const parts = rawDest.split(",").map((s) => s.trim()).filter(Boolean);
              return parts.length >= 2 ? parts.slice(-2).join(", ") : rawDest;
            })()
          : `${selectedAddress.city}, ${selectedAddress.state}`;
        routeLabels = {
          origin: originLabel,
          hub: `${hubDisplay.city}, ${hubDisplay.state}`,
          destination: destinationLabel,
        };
      }
      return (
        <>
          <PageLayout {...layoutProps}>
            <CheckoutShippingPage
              onBack={onNavigateBack}
              shippingOptions={shippingOptions}
              shippingOptionsLoading={shippingOptionsLoading}
              shippingOptionsError={shippingOptionsError}
              selectedShippingId={selectedShippingId}
              onSelectShipping={onSelectShipping}
              onContinue={() => {
                onSetReturnToPage?.("checkout-shipping");
                (onShippingContinueToReview ?? (() => onSetCurrentPage("checkout-review")))();
              }}
              routeLabels={routeLabels}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "checkout-payment": {
      const handlePaymentContinue = () => {
        // If Pix or Boleto, skip installments and go straight to review
        if (selectedPaymentId === "pix" || selectedPaymentId === "boleto") {
          onSetCurrentPage("checkout-review");
        } else {
          // For credit/debit cards, go to installments page
          onSetCurrentPage("checkout-installments");
        }
      };
      
      return (
        <>
          <PageLayout {...layoutProps}>
            <CheckoutPaymentPage
              onBack={onNavigateBack}
              paymentMethods={paymentMethods}
              selectedPaymentId={selectedPaymentId}
              onSelectPayment={onSelectPayment}
              onAddNewCard={onAddNewPaymentMethod}
              onContinue={handlePaymentContinue}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "checkout-installments": {
      // Calculate total amount
      const subtotal = cart.reduce((sum, item) => sum + item.ad.price * item.quantity, 0);
      const selectedShipping = shippingOptions.find(s => s.id === selectedShippingId);
      const shippingCost = selectedShipping?.price || 0;
      const totalAmount = subtotal + shippingCost;
      
      const handleInstallmentsContinue = (installments: number) => {
        onSelectInstallments(installments);
        onSetCurrentPage("checkout-review");
      };
      
      return (
        <>
          <PageLayout {...layoutProps}>
            <CheckoutInstallmentsPage
              onBack={onNavigateBack}
              totalAmount={totalAmount}
              onContinue={handleInstallmentsContinue}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "checkout-review": {
      // Pay existing order mode (from order-detail or resume): data comes from order API, confirm opens Stripe
      const orderIdToPay = payOrderId ?? cartOrderId;
      if (orderIdToPay && onPayOrderFromReview) {
        return (
          <>
            <PageLayout {...layoutProps}>
              <CheckoutReviewPage
                payOrderId={orderIdToPay}
                onBack={() => {
                  if (setPayOrderId) setPayOrderId(null);
                  onNavigateBack();
                }}
                onPayOrder={onPayOrderFromReview}
              />
            </PageLayout>
            {showFloatingChatButton && (
              <FloatingChatButton
                onClick={onOpenChatModal}
                unreadCount={totalUnreadMessages}
              />
            )}
          </>
        );
      }
      const selectedAddress = addresses.find(a => a.id === selectedAddressId);
      const selectedShipping = shippingOptions.find(s => s.id === selectedShippingId);
      const selectedPayment = paymentMethods.find(p => p.id === selectedPaymentId) ?? null;
      // Redirect is now handled by useEffect (address + shipping required; payment chosen on Stripe)
      if (!selectedAddress || !selectedShipping) {
        return null;
      }
      const hasHubRoutingFromOptionsReview = shippingOptions.some((o) => o.leg1_1 != null || o.leg2 != null);
      const isHubFlowFromCartReview = cart.some(
        (item: { isOfferItem?: boolean; offerTradeInAds?: unknown[] }) =>
          item.isOfferItem && Array.isArray(item.offerTradeInAds) && item.offerTradeInAds.length > 0
      );
      let reviewRouteLabels: ShippingRouteLabels | null = null;
      if ((hasHubRoutingFromOptionsReview || isHubFlowFromCartReview) && hubDisplay && selectedAddress) {
        const buyerOriginAddressReview = addresses.find((a) => a.isDefault) ?? addresses[0] ?? selectedAddress;
        const originLabelReview =
          [buyerOriginAddressReview.city, buyerOriginAddressReview.state].filter(Boolean).join(" / ") || "Origem";
        const offerItemReview = cart.find(
          (i: { isOfferItem?: boolean; offerTradeInAds?: unknown[]; ad?: { location?: string } }) =>
            i.isOfferItem && Array.isArray(i.offerTradeInAds) && i.offerTradeInAds.length > 0 && i.ad?.location
        ) as { ad: { location: string } } | undefined;
        const rawDestReview = offerItemReview?.ad?.location?.trim();
        const destinationLabelReview = rawDestReview
          ? (() => {
              const parts = rawDestReview.split(",").map((s) => s.trim()).filter(Boolean);
              return parts.length >= 2 ? parts.slice(-2).join(", ") : rawDestReview;
            })()
          : `${selectedAddress.city}, ${selectedAddress.state}`;
        reviewRouteLabels = {
          origin: originLabelReview,
          hub: `${hubDisplay.city}, ${hubDisplay.state}`,
          destination: destinationLabelReview,
        };
      }
      return (
        <>
          <PageLayout {...layoutProps}>
            <CheckoutReviewPage
              onBack={onNavigateBack}
              cartItems={cart}
              selectedAddress={selectedAddress}
              selectedPayment={selectedPayment}
              selectedShipping={selectedShipping}
              selectedInstallments={selectedInstallments}
              onConfirmOrder={onConfirmOrder}
              orderError={orderError ?? null}
              isConfirming={orderConfirming}
              routeLabels={reviewRouteLabels}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "checkout-stripe": {
      if (!pendingPaymentOrderId || !pendingPaymentClientSecret || !onPaymentSuccess) {
        return null;
      }
      return (
        <CheckoutStripePage
          orderId={pendingPaymentOrderId}
          clientSecret={pendingPaymentClientSecret}
          publishableKey={pendingPaymentPublishableKey ?? ""}
          onSuccess={onPaymentSuccess}
          onBack={onBackFromStripeCheckout ?? (() => onSetCurrentPage("checkout-review"))}
        />
      );
    }

    case "checkout-second-party": {
      if (!secondPartyPaymentOrderId) {
        onSetCurrentPage("order-detail");
        return null;
      }
      return (
        <CheckoutSecondPartyPage
          orderId={secondPartyPaymentOrderId}
          onBack={() => {
            setSecondPartyPaymentOrderId(null);
            onSetCurrentPage("order-detail");
          }}
          onPaymentIntentReady={(orderId, clientSecret, publishableKey) => {
            onSecondPartyPaymentIntentReady?.(orderId, clientSecret, publishableKey);
          }}
        />
      );
    }

    case "order-confirmation": {
      const deliveryDate = new Date();
      deliveryDate.setDate(deliveryDate.getDate() + 7);
      const formattedDate = deliveryDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      return (
        <>
          <PageLayout {...layoutProps}>
            <OrderConfirmationPage
              orderNumber={lastOrderNumber}
              onBackToHome={() => onSetCurrentPage("home")}
              estimatedDelivery={formattedDate}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "chat":
      return (
        <PageLayout {...layoutProps}>
          <ChatListPage
            onBack={onNavigateBack}
            conversations={conversations as Conversation[]}
            setConversations={setConversations}
            onSelectConversation={(id) => {
              onSetSelectedConversationId(id);
              onSetCurrentPage("chat-conversation");
            }}
            userId={userId}
            {...subPageCommonProps}
          />
        </PageLayout>
      );

    case "chat-conversation": {
      const conversation = conversations.find(c => c.id === selectedConversationId);
      if (!conversation) return null;
      
      // Filter messages for this conversation
      const conversationMessages = chatMessages.filter(
        msg => msg.conversationId === conversation.id
      );
      
      return (
        <PageLayout {...layoutProps}>
          <ChatPage
            onBack={onNavigateBack}
            conversationName={conversation.name}
            conversationType={conversation.type}
            adTitle={conversation.adTitle}
            otherPartyProfileImageUrl={conversation.avatar}
            conversationId={conversation.id}
            userId={userId}
            initialMessages={conversationMessages.length > 0 ? conversationMessages : undefined}
            onSendMessage={(message) => onSendMessage(conversation.id, message)}
            onViewOfferDetails={onViewOfferDetails}
            onThreadViewed={(id) =>
              setConversations((prev) =>
                prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c))
              )
            }
            {...subPageCommonProps}
          />
        </PageLayout>
      );
    }

    case "category-results":
      return (
        <>
          <PageLayout {...layoutProps}>
            <CategoryResultsPage
              category={selectedCategory || "Todos"}
              categoryDisplayName={categoryDisplayName}
              categoryCatalog={categoryCatalog}
              ads={searchResultsAds}
              useServerSearch
              onLoadMore={searchNextCursor ? loadMoreSearchResults : undefined}
              hasMore={!!searchNextCursor}
              searchResultsLoading={searchResultsLoading}
              searchQuery={searchQuery || ""}
              onSearchChange={onSearchChange || (() => {})}
              selectedCategory={selectedCategory || "Todos"}
              onCategoryChange={onCategoryChange || (() => {})}
              categories={categories || []}
              popularCategories={popularCategories}
              currentLocation={currentLocation}
              onLocationClick={onLocationClick}
              onSortClick={onSortClick}
              onCustomFilterClick={onCustomFilterClick}
              activeFiltersCount={activeFiltersCount}
              currentSort={currentSort || "newest"}
              customFilters={customFilters || { brands: [], conditions: [], priceMin: "", priceMax: "", locations: [] }}
              isFavorite={isFavorite}
              onToggleFavorite={wrapToggleFavorite}
              onAdClick={(adId) => {
                onSetReturnToPage?.(currentPage);
                onSetSelectedAdId(adId);
                onSetCurrentPage("ad-detail");
              }}
              onBackToHome={onBackToHome}
              onLogoClick={onBackToHome}
              cartCount={cart.length}
              notificationCount={notificationUnreadCount}
              onCartClick={() => onSetCurrentPage("cart")}
              onNotificationsClick={() => onSetCurrentPage("notifications")}
              onAddressClick={() => onSetCurrentPage("addresses")}
              headerUserName={userProfile?.preferredName?.trim() || userProfile?.fullName || ""}
              headerAddress={userProfile?.address ? `${userProfile.address.street}, ${userProfile.address.number}` : ""}
              onFavoritesClick={() => onSetCurrentPage("favorites")}
              onMyAdsClick={() => onSetCurrentPage("my-ads")}
              onWalletClick={() => onSetCurrentPage("wallet")}
              onNavigateToAbout={onNavigateToAbout}
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacyPolicy={onNavigateToPrivacyPolicy}
              onNavigateToHelp={() => onSetCurrentPage("help")}
              {...subPageCommonProps}
            />
          </PageLayout>
          
          {/* Location Selector Modal */}
          {isLocationSelectorOpen && onCloseLocationSelector && onSelectLocation && (
            <LocationSelectorModal
              isOpen={isLocationSelectorOpen}
              onClose={onCloseLocationSelector}
              currentLocation={selectedCity || { name: "Franca", state: "São Paulo", stateCode: "SP" }}
              onSelectLocation={onSelectLocation}
            />
          )}

          {/* Sort Sheet */}
          {isSortSheetOpen && onCloseSortSheet && onSortChange && (
            <SortSheet
              isOpen={isSortSheetOpen}
              onClose={onCloseSortSheet}
              currentSort={currentSort || "newest"}
              onSortChange={onSortChange}
            />
          )}

          {/* Custom Filter Sheet */}
          {isCustomFilterOpen && onCloseCustomFilter && onCustomFilterChange && onClearCustomFilters && (
            <CustomFilterSheet
              isOpen={isCustomFilterOpen}
              onClose={onCloseCustomFilter}
              customFilters={customFilters || { brands: [], conditions: [], priceMin: "", priceMax: "", locations: [] }}
              onCustomFilterChange={onCustomFilterChange}
              activeCustomFiltersCount={activeFiltersCount || 0}
              onClearCustomFilters={onClearCustomFilters}
            />
          )}
          
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "profile":
      return (
        <>
          <PageLayout {...layoutProps}>
            <ProfilePage
              onBack={onNavigateBack}
              onEditProfile={() => {
                onClearNavigatedFromMenu(); // Clear flag for nested navigation
                onSetCurrentPage("edit-profile");
              }}
              userProfile={userProfile}
              userEmail={userEmail}
              authUserName={authUserName}
              onProfileUpdated={onProfileUpdated}
              statsRefreshKey={myAdsRefreshKey}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "edit-profile":
      return (
        <>
          <PageLayout {...layoutProps}>
            <EditProfilePage
              onBack={onNavigateBack}
              onSave={async (profileData) => {
                if (onSaveProfile) await onSaveProfile(profileData);
                onSetCurrentPage("profile");
              }}
              onProfileUpdated={onProfileUpdated}
              initialData={userProfile ? {
                fullName: userProfile.fullName,
                preferredName: userProfile.preferredName ?? "",
                cpf: userProfile.cpf,
                email: userEmail || userProfile.email,
                phone: userProfile.phone,
                birthDate: userProfile.birthDate,
                gender: userProfile.gender ?? "prefer-not-to-say",
                address: {
                  street: userProfile.address.street,
                  number: userProfile.address.number,
                  complement: userProfile.address.complement,
                  neighborhood: userProfile.address.neighborhood,
                  city: userProfile.address.city,
                  state: userProfile.address.state,
                  cep: userProfile.address.cep,
                },
                profileImage: userProfile.profileImage,
              } : undefined}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "settings":
      return (
        <>
          <PageLayout {...layoutProps}>
            <SettingsPage
              onBack={onNavigateBack}
              userDisplayName={settingsCardName}
              userEmail={settingsCardEmail}
              userInitials={settingsCardInitials}
              userProfileImageUrl={userProfileImageUrl?.trim() || userProfile?.profileImage?.trim()}
              onNavigateToPasswordSecurity={onNavigateToPasswordSecurity}
              onNavigateToLanguageRegion={onNavigateToLanguageRegion}
              onNavigateToAbout={onNavigateToAbout}
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacyPolicy={onNavigateToPrivacyPolicy}
              onNavigateToPrivacy={onNavigateToPrivacy}
              onNavigateToAccountDeletionPublic={onNavigateToAccountDeletionPublic}
              isDarkMode={isDarkMode}
              onToggleDarkMode={onToggleDarkMode}
              onLogout={menuHandlers.onLogout}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "help":
      return (
        <>
          <PageLayout {...layoutProps}>
            <HelpSupportPage
              onBack={onNavigateBack}
              onNavigateToTrocaViaHub={() => onSetCurrentPage("troca-via-hub")}
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacyPolicy={onNavigateToPrivacyPolicy}
              {...subPageCommonProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "troca-via-hub":
      return (
        <>
          <PageLayout {...layoutProps}>
            <TrocaViaHubPage onBack={onNavigateBack} {...subPageCommonProps} />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "wallet":
      return (
        <>
          <PageLayout {...layoutProps}>
            <WalletPage
              onBack={onNavigateBack}
              onNavigateToWithdraw={() => onSetCurrentPage("withdraw")}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "password-security":
      return (
        <>
          <PageLayout {...layoutProps}>
            <PasswordSecurityPage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "payment-methods":
      return (
        <>
          <PageLayout {...layoutProps}>
            <PaymentMethodsPage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "withdraw":
      return (
        <>
          <PageLayout {...layoutProps}>
            <WithdrawPage
              onBack={onNavigateBack}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "language-region":
      return (
        <>
          <PageLayout {...layoutProps}>
            <LanguageRegionPage
              onBack={onNavigateBackToSettings}
              activeTab={commonPageProps.activeTab}
              onTabChange={commonPageProps.onTabChange}
              favoritesCount={commonPageProps.favoritesCount}
              onMenuClick={commonPageProps.onMenuClick}
              onPostAdClick={commonPageProps.onPostAdClick}
              postAdBlockedByUnverifiedEmail={commonPageProps.postAdBlockedByUnverifiedEmail}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "privacy":
      return (
        <>
          <PageLayout {...layoutProps}>
            <PrivacyPage onBack={onNavigateBackToSettings} {...commonPageProps} />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "data-usage":
      return (
        <>
          <PageLayout {...layoutProps}>
            <DataUsagePage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "about":
      return (
        <>
          <PageLayout {...layoutProps}>
            <AboutPage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "terms":
      return (
        <>
          <PageLayout {...layoutProps}>
            <TermsPage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "privacy-policy":
      return (
        <>
          <PageLayout {...layoutProps}>
            <PrivacyPolicyPage
              onBack={onNavigateBackToSettings}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "account-deletion":
      return (
        <>
          <PageLayout {...layoutProps}>
            <AccountDeletionPublicPage
              onBack={onNavigateBack}
              onOpenPrivacyPolicy={onNavigateToPrivacyPolicy}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "make-offer": {
      const ad = detailAd && detailAd.id === selectedAdId ? detailAd : ads.find(a => a.id === selectedAdId);
      if (!ad && !detailAdLoading) return null;
      if (detailAdLoading && !ad) {
        return (
          <PageLayout {...layoutProps}>
            <div className="flex min-h-[50vh] items-center justify-center">
              <p className="text-muted-foreground">Carregando…</p>
            </div>
          </PageLayout>
        );
      }
      if (!ad) return null;

      // Buyer's own ads for product-plus-cash (from GET /auth/me/ads; home feed does not include own ads)
      const userAds = myAdsForOffer.filter(a => a.id !== selectedAdId);
      
      return (
        <>
          <PageLayout {...layoutProps}>
            <MakeOfferPage
              targetAd={ad}
              userAds={userAds}
              userAdsLoading={myAdsForOfferLoading}
              onBack={onNavigateBack}
              onSubmitOffer={onSubmitOffer || (() => {})}
              isSubmittingOffer={isSubmittingOffer}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );
    }

    case "received-offers":
      return (
        <>
          <PageLayout {...layoutProps}>
            <ReceivedOffersPage
              {...commonPageProps}
              onBack={onNavigateBack}
              onAdClick={(adId) => {
                onSetReturnToPage?.(currentPage);
                onSetSelectedAdId(adId);
                onSetCurrentPage("ad-detail");
              }}
              receivedOffers={receivedOffers}
              loadReceivedOffers={loadReceivedOffers}
              onNotificationCountRefetch={onNotificationCountRefetch}
              offerIdsInCart={
                accountNav ? cart.filter((i) => i.offerId).map((i) => i.offerId!) : []
              }
              onRemoveOfferFromCart={
                accountNav
                  ? (offerId) => {
                      const item = cart.find((i) => i.offerId === offerId);
                      if (item) onRemoveFromCart(item.ad.id);
                    }
                  : undefined
              }
              onAddOfferToCart={
                accountNav
                  ? (offerId) => {
                      const offer = receivedOffers.find((o) => o.id === offerId);
                      if (offer && offer.status === "accepted") {
                        const cartItem = {
                          ad: offer.targetAd,
                          quantity: 1,
                          offerId: offer.id,
                          isOfferItem: true,
                          offerPrice: offer.cashAmount,
                          offerExpiration: undefined,
                          offerTradeInAds: offer.tradeInAds,
                        };
                        const existingItem = cart.find((item) => item.offerId === offerId);
                        if (existingItem) {
                          toast.info("Esta oferta já está no seu carrinho!");
                        } else {
                          onAddToCart(cartItem as any);
                        }
                      }
                    }
                  : undefined
              }
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "sent-offers":
      return (
        <>
          <PageLayout {...layoutProps}>
            <SentOffersPage
              onBack={onNavigateBack}
              onAdClick={(adId) => {
                onSetReturnToPage?.(currentPage);
                onSetSelectedAdId(adId);
                onSetCurrentPage("ad-detail");
              }}
              sentOffers={sentOffers}
              loadSentOffers={loadSentOffers}
              onNotificationCountRefetch={onNotificationCountRefetch}
              cartOrderId={cartOrderId}
              offerIdsInCart={
                accountNav ? cart.filter((i) => i.offerId).map((i) => i.offerId!) : []
              }
              onRemoveOfferFromCart={
                accountNav
                  ? (offerId) => {
                      const item = cart.find((i) => i.offerId === offerId);
                      if (item) onRemoveFromCart(item.ad.id);
                    }
                  : undefined
              }
              onAddOfferToCart={
                accountNav
                  ? (offerId) => {
                      const offer = sentOffers.find((o) => o.id === offerId);
                      if (offer && offer.status === "accepted") {
                        const cartItem = {
                          ad: offer.targetAd,
                          quantity: 1,
                          offerId: offer.id,
                          isOfferItem: true,
                          offerPrice: offer.cashAmount,
                          offerExpiration: offer.expiresAt,
                          offerTradeInAds: offer.tradeInAds,
                        };
                        const existingItem = cart.find((item) => item.offerId === offerId);
                        if (existingItem) {
                          toast.info("Esta oferta já está no seu carrinho!");
                        } else {
                          onAddToCart(cartItem as any);
                        }
                      }
                    }
                  : undefined
              }
              {...commonPageProps}
            />
            {accountNav && (
              <CartToast
                isOpen={showCartToast}
                onClose={onCloseCartToast}
                onGoToCart={() => {
                  onCloseCartToast();
                  onSetCurrentPage("cart");
                }}
                productName={cartToastProduct}
              />
            )}
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "sales":
      return (
        <>
          <PageLayout {...layoutProps}>
            <SalesPage
              {...commonPageProps}
              onBack={onNavigateBack}
              onOpenOrderDetail={(orderId: string) => onOpenOrderDetail(orderId, "sales")}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "purchases":
      return (
        <>
          <PageLayout {...layoutProps}>
            <PurchasesPage
              onBack={onNavigateBack}
              onOpenOrderDetail={onOpenOrderDetail}
              {...commonPageProps}
            />
          </PageLayout>
          {showFloatingChatButton && (
            <FloatingChatButton
              onClick={onOpenChatModal}
              unreadCount={totalUnreadMessages}
            />
          )}
        </>
      );

    case "order-detail":
      {
      const safeReturnPage: "purchases" | "sales" = orderDetailReturnPage === "sales" ? "sales" : "purchases";
      if (!selectedOrderId) {
        onSetCurrentPage(safeReturnPage);
        return null;
      }
      return (
        <>
          <OrderDetailPage
            orderId={selectedOrderId}
            onBack={() => {
              onSetSelectedOrderId(null);
              onSetCurrentPage(safeReturnPage);
            }}
            isSellerView={safeReturnPage === "sales"}
            onPayOrder={onGoToCheckoutToPayOrder ?? onPayPendingOrder}
            onStartSecondPartyCheckout={(orderId) => {
              setSecondPartyPaymentOrderId(orderId);
              onSetCurrentPage("checkout-second-party");
            }}
          />
        </>
      );
      }

    default:
      return null;
  }
}